import { graphql, readInlineData } from 'react-relay/legacy';

import {
    freeShippingHelpers_item as Item,
    freeShippingHelpers_item$key as ItemRef,
    VerticalType,
} from './__generated__/freeShippingHelpers_item.graphql';

const REST_OF_WORLD_REGION = 'Rest of the World';
const FREE_SHIPPING_PRICE = 0;
const JEWELRY_VERTICAL = 'jewelry';

function getItemData(itemReference: ItemRef): Item {
    return readInlineData<Item>(
        graphql`
            fragment freeShippingHelpers_item on Item @inline {
                vertical
                shipmentQuotes {
                    isComplimentary
                    totalAmount {
                        amount
                    }
                    serviceMethod {
                        region {
                            regionName
                        }
                    }
                }
            }
        `,
        itemReference
    );
}

type WorldShipping = {
    vertical: VerticalType | string;
    regionName: string;
    hasFreeShipping: boolean;
};
// FINDING-14549: Rest of world (ROW) shipping quotes are not considered in S/B free shipping, since items may
// have free shiping to ROW but not to user specifict region. However, few items in jewelry fall in to that
// situation and due to big marketing pushes around jewelry free shipping, we are temporarilyconsidering ROW quotes
// for jewelry vertical only on the FE until we revisit how free shipping is determined.
function isEligibleRestOfWorldFreeShipping({
    vertical,
    regionName,
    hasFreeShipping,
}: WorldShipping): boolean {
    return hasFreeShipping && vertical === JEWELRY_VERTICAL && regionName === REST_OF_WORLD_REGION;
}

export type UserRegions = ReadonlyArray<{
    readonly displayName: string | null;
} | null> | null;

type RegionShipping = {
    readonly userRegions: UserRegions;
    regionName: string;
    hasFreeShipping: boolean;
};

function isEligibleUserRegionFreeShipping({
    userRegions,
    regionName,
    hasFreeShipping,
}: RegionShipping): boolean {
    const userRegionNames = (userRegions || []).map(region => region?.displayName).filter(Boolean);
    return hasFreeShipping && userRegionNames.includes(regionName);
}

type FreeShipping = {
    item: ItemRef;
    readonly userRegions: UserRegions;
};

// userRegions will only populate for non-private IPs. If you are in office or vpn, manually set zipCode in sessionStorage userGeoInfo
export const getIsFreeShipping = ({ item, userRegions }: FreeShipping): boolean => {
    const itemData = getItemData(item);
    const shipmentQuotes = itemData?.shipmentQuotes || [];
    return !!shipmentQuotes.find(shipmentQuote => {
        const hasFreeShipping =
            shipmentQuote?.isComplimentary ||
            shipmentQuote?.totalAmount?.amount === FREE_SHIPPING_PRICE;
        const regionName = shipmentQuote?.serviceMethod?.region?.regionName || '';
        return (
            isEligibleUserRegionFreeShipping({
                userRegions,
                regionName,
                hasFreeShipping,
            }) ||
            isEligibleRestOfWorldFreeShipping({
                vertical: itemData?.vertical || '',
                regionName,
                hasFreeShipping,
            })
        );
    });
};
